import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import type { PageGuides } from '../editor/pageFormat';
import {
  dropGuide, GUIDE_GRAB_MOUSE, GUIDE_GRAB_TOUCH, GUIDE_SNAP_PX, guideAt, pxPerUnit, RULER_CHOICES, RULER_PX, rulerUnit,
  snapTo, tickLabel, tickPlan, type RulerChoice, type RulerUnit
} from '../editor/rulerMath';
import { $ } from './dom';
import { toast } from './toast';

/*
 * ─── ไม้บรรทัด + เส้นไกด์ ────────────────────────────────────────────────────────────────────────────────────────────
 * Choices 1ก 2ก 3ก 4ก (src/editor/rulerMath.ts). Two rulers over the stage (top and left, beside a tablet's side panel);
 * the editor keeps their room free when it fits the page (ed.setRulerSpace).
 *   - drag from the top ruler → a horizontal guide, from the left ruler → a vertical one; it snaps to the page's
 *     edges/middle and to objects' edges; its position is shown while dragging;
 *   - a guide on the page is picked up where no object is under the finger (objects win: they often sit on a guide)
 *     and moved; dropped back on its ruler it is removed;
 *   - the corner switches the unit: อัตโนมัติ (a print page → mm, else px) → px → mm → cm;
 *   - เพิ่มเติม → "ไม้บรรทัดและเส้นไกด์" shows/hides them (remembered on this device; phones start with them off);
 *     "ลบเส้นไกด์ในหน้านี้" removes the page's guides (one undo step).
 * Every change of the guides goes through history.setPageGuides (one undo step, saved in the work); a change that
 * cannot be made says why.
 */

const SHOW_KEY = 'craftstudio.rulers.v1';
const UNIT_KEY = 'craftstudio.rulerUnit.v1';
const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* not remembered on this device */ } };

const UNIT_TH: Record<RulerChoice, string> = { auto: 'อัตโนมัติ', px: 'พิกเซล (px)', mm: 'มิลลิเมตร (mm)', cm: 'เซนติเมตร (cm)' };

export function initRulers(ed: Editor, history: History, opts: {
  canEdit: () => boolean;
  /** a drawing tool is in use (pen, shapes…): the page takes the finger, guides are not picked up */
  toolBusy: () => boolean;
}) {
  const { canvas } = ed;
  /** Fabric's top canvas: where presses land (its type is missing from @types/fabric) */
  const upper = () => (canvas as unknown as { upperCanvasEl: HTMLCanvasElement }).upperCanvasEl;
  const stage = $('stage'), box = $('rulers');
  const top = $('rulTop') as HTMLCanvasElement, left = $('rulLeft') as HTMLCanvasElement, corner = $('rulCorner');
  const app = document.querySelector('.app') as HTMLElement;
  const phone = !matchMedia('(min-width: 900px)').matches;
  let shown = (read(SHOW_KEY) ?? (phone ? '0' : '1')) === '1';
  let choice: RulerChoice = (RULER_CHOICES as string[]).includes(read(UNIT_KEY) ?? '') ? read(UNIT_KEY) as RulerChoice : 'auto';
  const unit = (): RulerUnit => rulerUnit(choice, ed.doc.bleedMm);

  /* ─── show / hide ─── */
  function setShown(on: boolean, remember = true) {
    shown = on;
    if (remember) write(SHOW_KEY, on ? '1' : '0');
    box.hidden = !on;
    app.classList.toggle('rulers-on', on);
    ed.setRulerSpace(on ? RULER_PX : 0);
    paint(true);
  }

  /* ─── drawing the rulers ─── */
  let lastKey = '';
  function sizeCanvas(c: HTMLCanvasElement, w: number, h: number) {
    const dpr = window.devicePixelRatio || 1;
    const W = Math.max(1, Math.round(w * dpr)), H = Math.max(1, Math.round(h * dpr));
    if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
    c.style.width = `${w}px`; c.style.height = `${h}px`;
    const ctx = c.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
  }
  function paint(force = false) {
    if (!shown) return;
    const v = canvas.viewportTransform!, z = v[0], u = unit(), il = ed.insetLeftPx();
    const W = stage.clientWidth, H = stage.clientHeight;
    const sel = canvas.getActiveObject()?.getBoundingRect(true, true);
    const key = [v[0], v[4], v[5], u, il, W, H, choice, sel ? [sel.left, sel.top, sel.width, sel.height].join(',') : '', ed.doc.w, ed.doc.h].join('|');
    if (!force && key === lastKey) return;
    lastKey = key;
    box.style.setProperty('--rul-left', `${il}px`);
    corner.textContent = u;
    corner.title = `หน่วยไม้บรรทัด: ${UNIT_TH[choice]}${choice === 'auto' ? ` (${u})` : ''} — แตะเพื่อเปลี่ยน`;
    corner.setAttribute('aria-label', corner.title);
    corner.classList.toggle('auto', choice === 'auto');
    const st = getComputedStyle(document.documentElement);
    const col = (n: string, f: string) => st.getPropertyValue(n).trim() || f;
    const bg = col('--surface', '#fff'), ink = col('--muted', '#6b6880'), line = col('--line', '#ddd'), hi = col('--soft', '#eee6ff');
    const per = pxPerUnit(u), { major, parts } = tickPlan(z, u);
    const draw = (c: HTMLCanvasElement, len: number, horizontal: boolean) => {
      const ctx = horizontal ? sizeCanvas(c, len, RULER_PX) : sizeCanvas(c, RULER_PX, len);
      if (!ctx) return;
      ctx.fillStyle = bg; ctx.fillRect(0, 0, horizontal ? len : RULER_PX, horizontal ? RULER_PX : len);
      // where the ruler starts on the screen (css px from the stage's left/top) and the page's 0 there
      const offset = horizontal ? il + RULER_PX : RULER_PX;
      const origin = horizontal ? v[4] : v[5];
      const toScreen = (designPx: number) => designPx * z + origin - offset;
      // the page (and the chosen object) shaded
      ctx.fillStyle = hi;
      const p0 = toScreen(0), p1 = toScreen(horizontal ? ed.doc.w : ed.doc.h);
      if (horizontal) ctx.fillRect(p0, 0, p1 - p0, RULER_PX); else ctx.fillRect(0, p0, RULER_PX, p1 - p0);
      if (sel) {
        ctx.fillStyle = 'rgba(229,51,138,.18)';
        const a = toScreen(horizontal ? sel.left : sel.top), b = toScreen(horizontal ? sel.left + sel.width : sel.top + sel.height);
        if (horizontal) ctx.fillRect(a, 0, b - a, RULER_PX); else ctx.fillRect(0, a, RULER_PX, b - a);
      }
      ctx.strokeStyle = ink; ctx.fillStyle = ink; ctx.lineWidth = 1;
      ctx.font = '10px sans-serif'; ctx.textBaseline = 'top';
      const step = parts ? major / parts : major;
      const first = Math.floor(((-origin + offset) / z) / per / step) * step;
      const last = ((len - origin + offset) / z) / per;
      ctx.beginPath();
      for (let n = 0, val = first; val <= last + step && n < 2000; n++, val = first + n * step) {
        const at = Math.round(toScreen(val * per)) + 0.5;
        const isMajor = Math.abs(val / major - Math.round(val / major)) < 1e-6;
        const tick = isMajor ? RULER_PX : (parts >= 5 && Math.abs((val / major) * 2 - Math.round((val / major) * 2)) < 1e-6) ? 8 : 5;
        if (horizontal) { ctx.moveTo(at, RULER_PX); ctx.lineTo(at, RULER_PX - tick); }
        else { ctx.moveTo(RULER_PX, at); ctx.lineTo(RULER_PX - tick, at); }
        if (isMajor) {
          const label = tickLabel(Math.round(val / major) * major, major);
          if (horizontal) ctx.fillText(label, at + 3, 2);
          else { ctx.save(); ctx.translate(2, at + 3); ctx.rotate(Math.PI / 2); ctx.textBaseline = 'bottom'; ctx.fillText(label, 0, 0); ctx.restore(); }
        }
      }
      ctx.stroke();
      ctx.strokeStyle = line;
      ctx.beginPath();
      if (horizontal) { ctx.moveTo(0, RULER_PX - 0.5); ctx.lineTo(len, RULER_PX - 0.5); } else { ctx.moveTo(RULER_PX - 0.5, 0); ctx.lineTo(RULER_PX - 0.5, len); }
      ctx.stroke();
    };
    draw(top, Math.max(0, W - il - RULER_PX), true);
    draw(left, Math.max(0, H - RULER_PX), false);
  }
  canvas.on('after:render', () => paint());
  new ResizeObserver(() => paint(true)).observe(stage);

  corner.onclick = () => {
    choice = RULER_CHOICES[(RULER_CHOICES.indexOf(choice) + 1) % RULER_CHOICES.length];
    write(UNIT_KEY, choice);
    paint(true);
    toast(choice === 'auto'
      ? `ไม้บรรทัด: อัตโนมัติ — หน้านี้ใช้ ${unit()} (${(ed.doc.bleedMm ?? 0) > 0 ? 'มีระยะตัดตก = งานพิมพ์' : 'ไม่มีระยะตัดตก'})`
      : `ไม้บรรทัด: ${UNIT_TH[choice]} (mm/cm คิดที่ 300 dpi เท่ากับงานพิมพ์)`);
  };

  /* ─── dragging a guide ─── */
  interface Drag { axis: 'x' | 'y'; from: number | null; moved: boolean; touch: boolean; id: number }
  let drag: Drag | null = null;

  /** the pointer (client px) → the guide's position in design px, snapped, and whether it would be removed */
  function place(axis: 'x' | 'y', cx: number, cy: number) {
    const v = canvas.viewportTransform!, z = v[0], r = upper().getBoundingClientRect(), s = stage.getBoundingClientRect();
    const raw = axis === 'x' ? (cx - r.left - v[4]) / z : (cy - r.top - v[5]) / z;
    const snapped = snapTo(raw, ed.guideSnapLines(axis), GUIDE_SNAP_PX / z);
    // removed when let go back over its own ruler (the left ruler starts after a tablet's side panel)
    const along = axis === 'x' ? cx - s.left - ed.insetLeftPx() : cy - s.top;
    // whole design px, unless it landed exactly on a line (an object's edge can be between pixels)
    return { v: snapped.snapped ? snapped.v : Math.round(snapped.v), remove: dropGuide(along) === 'remove' };
  }
  function show(axis: 'x' | 'y', cx: number, cy: number) {
    const p = place(axis, cx, cy);
    ed.setDragGuide({ axis, v: p.v, unit: unit(), remove: p.remove });
    return p;
  }
  /** why a guide cannot be added or moved now, or null */
  function blocked(): string | null {
    if (!opts.canEdit()) return 'งานนี้เปิดดูอย่างเดียว วางหรือย้ายเส้นไกด์ไม่ได้';
    if (history.loading) return 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่';
    return null;
  }
  function finish(cx: number, cy: number) {
    const d = drag; drag = null;
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointercancel', onCancel, true);
    ed.setDragGuide(null);
    if (!d) return;
    if (!d.moved && d.from === null) {
      toast(d.axis === 'y' ? 'ลากจากไม้บรรทัดด้านบนลงมาบนหน้างาน เพื่อวางเส้นไกด์แนวนอน' : 'ลากจากไม้บรรทัดด้านซ้ายเข้ามาบนหน้างาน เพื่อวางเส้นไกด์แนวตั้ง');
      return;
    }
    if (!d.moved) return;   // a guide picked up and let go where it was
    const p = place(d.axis, cx, cy);
    const g: PageGuides = history.pageGuides();
    const list = g[d.axis];
    if (d.from !== null) list.splice(d.from, 1);
    if (!p.remove) list.push(p.v);
    else if (d.from === null) return;   // dragged off a ruler and back onto it: nothing was added
    const why = history.setPageGuides(g);
    if (why) toast(`${p.remove ? 'ลบ' : d.from === null ? 'วาง' : 'ย้าย'}เส้นไกด์ไม่ได้: ${why}`, true);
    else if (p.remove) toast('ลบเส้นไกด์แล้ว (เลิกทำได้)');
  }
  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    e.preventDefault(); e.stopPropagation();
    drag.moved = true;
    show(drag.axis, e.clientX, e.clientY);
  };
  const onUp = (e: PointerEvent) => { if (drag && e.pointerId === drag.id) { e.preventDefault(); e.stopPropagation(); finish(e.clientX, e.clientY); } };
  const onCancel = (e: PointerEvent) => { if (drag && e.pointerId === drag.id) cancel(); };
  /** stop a drag without changing anything (a second finger came down: that is a pinch) */
  function cancel() {
    if (!drag) return;
    drag = null;
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointercancel', onCancel, true);
    ed.setDragGuide(null);
  }
  function start(axis: 'x' | 'y', from: number | null, e: PointerEvent) {
    drag = { axis, from, moved: false, touch: e.pointerType === 'touch', id: e.pointerId };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onCancel, true);
  }

  // off a ruler: top → horizontal guide (y), left → vertical guide (x)
  const fromRuler = (axis: 'x' | 'y') => (e: PointerEvent) => {
    if (e.button > 0 || drag) return;
    e.preventDefault();
    const why = blocked();
    if (why) { toast(why, true); return; }
    canvas.discardActiveObject(); canvas.requestRenderAll();
    start(axis, null, e);
  };
  top.addEventListener('pointerdown', fromRuler('y'));
  left.addEventListener('pointerdown', fromRuler('x'));

  /**
   * a guide on the page: picked up where no object is under the pointer — before Fabric sees the press (capture on
   * the stage; Fabric 5 listens to mouse/touch events, which a cancelled pointerdown's mouse events and an
   * un-propagated touchstart never reach it with)
   */
  function guideUnder(cx: number, cy: number, touch: boolean): { axis: 'x' | 'y'; k: number } | null {
    if (!shown || drag || opts.toolBusy() || canvas.isDrawingMode) return null;
    const v = canvas.viewportTransform!, z = v[0], r = upper().getBoundingClientRect();
    const x = (cx - r.left - v[4]) / z, y = (cy - r.top - v[5]) / z;
    const th = (touch ? GUIDE_GRAB_TOUCH : GUIDE_GRAB_MOUSE) / z;
    const g = history.pageGuides();
    const kx = guideAt(g.x, x, th), ky = guideAt(g.y, y, th);
    if (kx < 0 && ky < 0) return null;
    // an object (or the chosen object's handles) under the pointer wins: objects often sit right on a guide
    const fake = { clientX: cx, clientY: cy, target: upper() } as unknown as Event;
    if (canvas.findTarget(fake, false)) return null;
    if (kx >= 0 && (ky < 0 || Math.abs(g.x[kx] - x) <= Math.abs(g.y[ky] - y))) return { axis: 'x', k: kx };
    return { axis: 'y', k: ky };
  }
  let picked: { axis: 'x' | 'y'; k: number } | null = null;
  stage.addEventListener('pointerdown', e => {
    if (e.target !== upper() || !e.isPrimary || e.button > 0) return;
    const hit = guideUnder(e.clientX, e.clientY, e.pointerType !== 'mouse');
    if (!hit) return;
    const why = blocked();
    if (why) { toast(why, true); return; }
    picked = hit;
    e.preventDefault(); e.stopPropagation();   // no mouse events follow for a mouse; touches are stopped below
    start(hit.axis, hit.k, e);
    show(hit.axis, e.clientX, e.clientY);
    // the guide being moved is drawn as the dragged one, not twice
    const g = history.pageGuides(); g[hit.axis].splice(hit.k, 1); ed.setPageGuides(g);
  }, { capture: true });
  // Fabric listens to touchstart: keep the touch that picked a guide away from it (it would start a selection box)
  stage.addEventListener('touchstart', e => {
    if (e.touches.length > 1 && drag) {
      // a second finger: a pinch — the guide goes back where it was
      cancel();
      if (picked) { ed.setPageGuides(history.pageGuides()); picked = null; }
      return;
    }
    if (drag && picked) { e.stopPropagation(); }
  }, { capture: true });
  // after a pick-up the page shows the guides without the moving one: put them back from history when it ends
  const restore = () => { if (picked && !drag) { picked = null; ed.setPageGuides(history.pageGuides()); } };
  window.addEventListener('pointerup', () => setTimeout(restore, 0), true);
  window.addEventListener('pointercancel', () => setTimeout(restore, 0), true);

  // a mouse over a guide (where no object is) shows that it can be moved
  canvas.on('mouse:move', o => {
    const e = o.e as MouseEvent;
    if (o.target || drag || !(e instanceof MouseEvent) || e.buttons) return;
    const hit = guideUnder(e.clientX, e.clientY, false);
    if (hit) canvas.setCursor(hit.axis === 'x' ? 'col-resize' : 'row-resize');
  });

  /* ─── เพิ่มเติม ─── */
  const sw = $('dRulers') as HTMLInputElement, clearBtn = $('dGuidesClear') as HTMLButtonElement;
  function syncSheet() {
    sw.checked = shown;
    const n = history.pageGuides(), here = n.x.length + n.y.length, all = history.guideCount();
    clearBtn.disabled = here === 0;
    clearBtn.textContent = here ? `ลบเส้นไกด์ในหน้านี้ (${here})` : 'ลบเส้นไกด์ในหน้านี้';
    $('dGuidesInfo').textContent = !shown && all
      ? `งานนี้มีเส้นไกด์ ${all} เส้น ซ่อนอยู่พร้อมไม้บรรทัด (ชิ้นงานไม่ดูดติดเส้นที่ซ่อน)`
      : 'ลากจากไม้บรรทัดเพื่อวางเส้นไกด์ · ลากกลับเข้าไม้บรรทัดเพื่อลบ · เส้นไกด์บันทึกไว้ในงานแต่ละหน้า ไม่ติดไปในไฟล์ที่ส่งออก';
  }
  sw.onchange = () => { setShown(sw.checked); syncSheet(); };
  clearBtn.onclick = () => {
    const why = blocked();
    if (why) { toast(why, true); return; }
    const n = history.clearGuides(false);
    if (n) toast(`ลบเส้นไกด์ ${n} เส้นในหน้านี้แล้ว (เลิกทำได้)`);
    else toast('หน้านี้ไม่มีเส้นไกด์ให้ลบ', true);
    syncSheet();
  };

  setShown(shown, false);
  return {
    /** refresh the เพิ่มเติม rows (when the panel opens) */
    syncSheet,
    /** the page or its size changed: draw the rulers again */
    refresh: () => paint(true),
    get shown() { return shown; }
  };
}
